import { makeStyles, Theme } from '@material-ui/core/styles'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useState } from 'react'
import InsightsPanel from './components/InsightsPanel'
import ReportsPanel from './components/ReportsPanel'
import ReportsSubTabNav, {
    ReportsSubTab,
} from './components/ReportsSubTabNav'

/**
 * Height of the sub-tab row. The reports content sizes itself against the
 * viewport, so it has to reserve this space when the row is displayed.
 */
const SUB_TAB_ROW_HEIGHT_PX = 48

const useStyles = makeStyles((theme: Theme) => ({
    subTabNavContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}))

/** Props for {@link ProjectReportsPage}. Forwarded to the Reports sub-tab. */
export interface ProjectReportsPageProps {
    noElementsAddedProp?: boolean
    selectedAssembliesProp?: string[]
    showReportTypeDialogProp?: boolean
}

/**
 * The project Reports page.
 *
 * A shell that switches between two sub-tabs: "Reports" (report generation and
 * the results table) and "Insights". The Insights sub-tab is gated behind
 * `REACT_APP_ShowSpecIntelligence`; when that flag is off no sub-tab row renders
 * at all and the page looks exactly as it did before sub-tabs existed.
 *
 * The Reports panel deliberately stays mounted while Insights is showing, so an
 * in-flight report generation and its cached results survive a tab switch.
 */
const ProjectReportsPage = ({
    noElementsAddedProp,
    selectedAssembliesProp,
    showReportTypeDialogProp,
}: ProjectReportsPageProps) => {
    const classes = useStyles()
    const [activeSubTab, setActiveSubTab] = useState(ReportsSubTab.Reports)

    const showInsightsSubTab =
        processEnv.REACT_APP_ShowSpecIntelligence === '1'
    const isInsightsActive = activeSubTab === ReportsSubTab.Insights
    const isReportsActive = !isInsightsActive

    return (
        <>
            {showInsightsSubTab && (
                <div className={classes.subTabNavContainer}>
                    <ReportsSubTabNav
                        value={activeSubTab}
                        onChange={setActiveSubTab}
                    />
                </div>
            )}

            <div
                role="tabpanel"
                hidden={!isReportsActive}
                id={`reportsSubTabPanel-${ReportsSubTab.Reports}`}
                aria-labelledby={`reportsSubTab-${ReportsSubTab.Reports}`}
            >
                <ReportsPanel
                    contentHeightOffset={
                        showInsightsSubTab ? SUB_TAB_ROW_HEIGHT_PX : 0
                    }
                    noElementsAddedProp={noElementsAddedProp}
                    selectedAssembliesProp={selectedAssembliesProp}
                    showReportTypeDialogProp={showReportTypeDialogProp}
                />
            </div>

            <div
                role="tabpanel"
                hidden={!isInsightsActive}
                id={`reportsSubTabPanel-${ReportsSubTab.Insights}`}
                aria-labelledby={`reportsSubTab-${ReportsSubTab.Insights}`}
            >
                {isInsightsActive && <InsightsPanel />}
            </div>
        </>
    )
}

export default ProjectReportsPage
